/**
 * Performs a real network request without reading from or writing to the
 * browser HTTP cache. Keep this as the single fetch boundary for API calls.
 */
interface FetchStartState {
	lastStartedAt: number
	queuedStarts: number
	queue: Promise<void>
}

const fetchStartStates = new Map<string, FetchStartState>()

const resourceKey = (input: RequestInfo | URL) => {
	if (typeof input === "string") return input
	if (input instanceof URL) return input.href
	return input.url
}

const reserveDistinctFetchStart = (state: FetchStartState) => {
	state.queuedStarts += 1
	const reservation = state.queue.then(async () => {
		const delay = state.lastStartedAt - Date.now() + 1
		if (delay > 0) {
			await new Promise<void>((resolve) => window.setTimeout(resolve, delay))
		}
		state.lastStartedAt = Date.now()
	})

	// Only the sub-millisecond request start is serialized. Responses continue
	// in parallel. Capacitor's native fetch bridge uses the current millisecond
	// as its console timer id, so equal start times otherwise create false
	// duplicate/missing timer warnings for concurrent dashboard requests.
	state.queue = reservation.catch(() => undefined)
	return reservation
}

export const fetchFresh: typeof fetch = (input, init) => {
	const key = resourceKey(input)
	const state = fetchStartStates.get(key) ?? {
		lastStartedAt: 0,
		queuedStarts: 0,
		queue: Promise.resolve()
	}
	fetchStartStates.set(key, state)

	const startFetch = () =>
		globalThis.fetch(input, {
			...init,
			cache: "no-store"
		})

	const now = Date.now()
	if (state.queuedStarts === 0 && now > state.lastStartedAt) {
		state.lastStartedAt = now
		return startFetch()
	}

	return reserveDistinctFetchStart(state)
		.then(startFetch)
		.finally(() => {
			state.queuedStarts -= 1
		})
}
